/**
 * home_layout.ts — WHERE a versona lives inside their dwelling (PURE — no
 * three, no DOM; node-testable).
 *
 * The server keeps versonas at a household-shared home ANCHOR (families under
 * one roof — spreadSettlementLife's family law); the client's life layer calls
 * `interiorHomeSpot` to seat/bed each resident on a real spot INSIDE the
 * enterable building the anchor falls in: sleepers on the floor-1 bed (and
 * bedrolls beside it), children playing around the living-room rug, resters in
 * the sofa conversation grouping. Deterministic per (versona, building, state)
 * so every client agrees and nothing jitters frame to frame.
 *
 * POSITION MIRROR: the bed / sofa / table / rug coordinates below mirror the
 * FIXED slot positions in BuildingInteriorModule.furnishVersonaHome's planFor
 * (only the furniture MODEL picks are random there, never the positions) —
 * change one, change both.
 */

export interface HomeSpecLike {
  floors: number;
  hw: number;
  hd: number;
  stair: { x: number; z: number; w: number; d: number };
}

export interface HomeSpot {
  /** Building-local coords (the enterable's frame; toWorld rotates by b.yaw). */
  lx: number;
  lz: number;
  /** Which storey (slab y = floor === 0 ? 0.08 : floor * 3.4). */
  floor: number;
  /** Local yaw the body faces (add b.yaw for world). */
  yaw: number;
  /** Lift above the slab (bed height, seat height, bedroll pad). */
  lift: number;
  /** Reclined pose (sleeping on a bed/bedroll). */
  lying: boolean;
}

/** FNV-1a over a string with a murmur-style finalizer (the HASH LAW: bare
 *  FNV-1a is last-character-weak — consecutive ids cluster without it). */
function h01(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Keep a spot inside the walkable core, out of the door corridor and the
 *  stair zone (mirrors furnishVersonaHome's clearOf). */
function legal(spec: HomeSpecLike, lx: number, lz: number): boolean {
  if (Math.abs(lx) < 1.4 && lz < -spec.hd + 3.4) return false; // door corridor (door is on -Z)
  const st = spec.stair;
  if (lx > st.x - 2.0 && lx < st.x + 2.0 && lz > st.z - st.d / 2 - 1.8 && lz < st.z + st.d / 2 + 1.8) return false;
  return Math.abs(lx) < spec.hw - 0.6 && Math.abs(lz) < spec.hd - 0.6;
}

/** Nudge an illegal spot toward the room center until it clears. */
function settle(spec: HomeSpecLike, lx: number, lz: number): { lx: number; lz: number } {
  let x = lx, z = lz;
  for (let i = 0; i < 6 && !legal(spec, x, z); i++) { x *= 0.72; z = z * 0.72 + 0.35; }
  return legal(spec, x, z) ? { lx: x, lz: z } : { lx: 0, lz: Math.min(spec.hd - 1, 1.2) };
}

/**
 * The deterministic interior spot for one resident in one home state.
 * `state`: 'sleeping' → the bed (first sleeper) / bedrolls (the rest);
 * 'playing' → around the living-room rug; anything else ('resting',
 * 'chatting') → the sofa conversation grouping on the ground floor.
 */
export function interiorHomeSpot(versonaId: string, buildingId: string, spec: HomeSpecLike, state: string): HomeSpot {
  const hw = spec.hw, hd = spec.hd;
  const ha = h01(`${versonaId}|${buildingId}|a`);
  const hb = h01(`${versonaId}|${buildingId}|b`);

  if (state === 'sleeping') {
    // Sleeping floor: storey 1 when the home has one (the themed bedroom),
    // else the ground floor of a one-room cottage.
    const floor = spec.floors > 1 ? 1 : 0;
    const k = Math.floor(ha * 4); // which sleeping spot this resident claims
    if (k === 0) {
      // The bed (planFor f===1: bed at (1.7, hd-1.35), yaw π — head to +Z).
      const p = settle(spec, 1.7, hd - 1.7);
      return { lx: p.lx, lz: p.lz, floor, yaw: Math.PI, lift: 0.5, lying: true };
    }
    // Bedrolls fan along the -X side of the bedroom.
    const p = settle(spec, -1.0 - (k - 1) * 1.35, hd - 1.7 - hb * 0.8);
    return { lx: p.lx, lz: p.lz, floor, yaw: Math.PI + (hb - 0.5) * 0.5, lift: 0.14, lying: true };
  }

  if (state === 'playing') {
    // Children play around the living-room rug (planFor f===0: rug at
    // (hw-2.4, -hd*0.25)) — a small ring so siblings tumble together.
    const ang = ha * Math.PI * 2;
    const r = 0.9 + hb * 0.9;
    const p = settle(spec, hw - 2.4 + Math.cos(ang) * r, -hd * 0.25 + Math.sin(ang) * r);
    return { lx: p.lx, lz: p.lz, floor: 0, yaw: ang + Math.PI, lift: 0, lying: false };
  }

  // Resting / chatting: the conversation grouping — first resident ON the
  // sofa (planFor f===0: seating at (hw-1.0, -hd*0.25) facing -X), the rest
  // stand around the low table 1.4 m out.
  if (ha < 0.4) {
    const p = settle(spec, hw - 1.15, -hd * 0.25 + (hb - 0.5) * 1.2);
    return { lx: p.lx, lz: p.lz, floor: 0, yaw: -Math.PI / 2, lift: 0.26, lying: false };
  }
  const ang = hb * Math.PI * 2;
  const p = settle(spec, hw - 2.45 + Math.cos(ang) * 1.25, -hd * 0.25 + Math.sin(ang) * 1.25);
  // Face the table so the family reads as gathered, not scattered.
  return { lx: p.lx, lz: p.lz, floor: 0, yaw: Math.atan2((hw - 2.45) - p.lx, (-hd * 0.25) - p.lz), lift: 0, lying: false };
}

/** The states the life layer treats as AT-HOME (indoors when the anchor falls
 *  inside an enterable dwelling). 'chatting' is what 'resting' becomes when
 *  family is near — the arrival-company remap. */
export const HOME_STATES: ReadonlySet<string> = new Set(['resting', 'sleeping', 'playing', 'chatting']);
